import React, { useEffect, useContext, useRef, useState } from "react"
import { FirebaseContext } from "../../Firebase"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { useIntl } from "react-intl"
import { navigate } from "gatsby"
import { FormattedMessage } from "react-intl"
import DOMPurify from "dompurify"
import { WingsEyebrow, WingsHeading, WingsDetailText } from "@wingspans-ui"

import TimeAgo from "javascript-time-ago"
import en from "javascript-time-ago/locale/en"
import es from "javascript-time-ago/locale/es"
TimeAgo.addLocale(es)
TimeAgo.addDefaultLocale(en)

const Notifications = ({ setShowDropdown, focusBellButton }) => {
  const { locale } = useIntl()

  const {
    localFirestore: { notificationsRecord },
  } = useContext(FirebaseContext)

  useEffect(() => {
    function clearSelectedText() {
      // bug fix to prevent text from being highlighted in the notifications list
      if (typeof window !== "undefined" && window.getSelection) {
        // Get the selection object
        var selection = window.getSelection()
        // Remove all ranges from the selection
        selection.removeAllRanges()
      } else if (document.selection) {
        // For older versions of Internet Explorer
        document.selection.empty()
      }
    }
    clearSelectedText()
  }, [])

  return (
    <>
      {
        <div className="notifications-list">
          {notificationsRecord.filter((notification) => !notification.isRead).length > 0 && (
            <>
              <div className="notifications-list-header">
                <WingsEyebrow as="p">
                  <FormattedMessage id="notifications-new" defaultMessage="What's new" />
                </WingsEyebrow>
              </div>

              {notificationsRecord
                .filter((notification) => !notification.isRead)
                .map((notification, index) => (
                  <React.Fragment key={`notification-${index}`}>
                    <ListNotification
                      notification={notification}
                      index={index}
                      locale={locale}
                      setShowDropdown={setShowDropdown}
                      focusBellButton={focusBellButton}
                    />
                  </React.Fragment>
                ))}
            </>
          )}

          {notificationsRecord.filter((notification) => notification.isRead).length > 0 && (
            <>
              <div className="notifications-list-header">
                <WingsEyebrow as="p">
                  <FormattedMessage id="notifications-past" defaultMessage="Past Notifications" />
                </WingsEyebrow>
              </div>

              {notificationsRecord
                .filter((notification) => notification.isRead)
                .map((notification, index) => (
                  <React.Fragment key={`notification-${index}`}>
                    <ListNotification
                      notification={notification}
                      index={index}
                      locale={locale}
                      setShowDropdown={setShowDropdown}
                      focusBellButton={focusBellButton}
                    />
                  </React.Fragment>
                ))}
            </>
          )}

          {notificationsRecord.length === 0 && (
            <div className={`notifications-list-item empty`} style={{ textAlign: "center" }}>
              <label>No notifications yet</label>
            </div>
          )}
        </div>
      }
    </>
  )
}

const ListNotification = ({ notification = "", index, locale = "en-US", setShowDropdown, focusBellButton }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const itemRef = useRef(null)
  const deleteButtonRef = useRef(null)
  const {
    localFirestore: { deleteUserNotification },
  } = useContext(FirebaseContext)

  // destriucture notification object found in firebase userNotifications collection
  const { message, messageEs, type, publicationDate, notificationId, isRead, pageLink, heading, headingEs } = notification
  const isNavigational = !!pageLink

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  const handleDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()
    deleteUserNotification(notificationId)
  }

  // Create formatter (English).
  const timeAgo = locale === "es-ES" ? new TimeAgo("es-ES") : new TimeAgo("en-US")

  const displayTime = timeAgo.format(publicationDate.toDate()) || "long ago"

  function CleanHTML({ dirtyHTML }) {
    if (!dirtyHTML) return ""

    // Sanitize the text
    const sanitizedText = DOMPurify.sanitize(dirtyHTML)

    // OLD CODE for bolding numbers, keeping in case we need to revert

    // // Define a regular expression to match numbers
    // const regex = /(\d+)/g

    // // Split the text into parts based on the regex
    // const parts = sanitizedText.split(regex)

    // // Map through the parts, wrapping numbers in <strong> tags
    // const formattedText = parts.map((part, index) => {
    //   if (part.match(regex)) {
    //     return <span key={index}>{part}</span>
    //   } else {
    //     return part
    //   }
    // })

    return <div dangerouslySetInnerHTML={{ __html: sanitizedText }} />
  }

  const handleNavigation = (e) => {
    e.preventDefault()
    if (isNavigational) {
      const localizedLink = locale === "es-ES" ? `/es-ES${notification.pageLink}` : notification.pageLink
      navigate(localizedLink)
      setShowDropdown(false)
    }
  }

  const handleItemFocus = () => {
    setIsFocused(true)
  }

  const handleItemBlur = (e) => {
    if (itemRef.current?.contains(e.relatedTarget)) return
    setIsFocused(false)
  }

  const handleItemKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault()
      e.stopPropagation()
      deleteButtonRef.current?.focus()
      return
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      e.stopPropagation()

      const allItems = Array.from(document.querySelectorAll(".notifications-list-item:not(.empty)"))
      const currentIndex = allItems.indexOf(itemRef.current)
      if (currentIndex === -1) return

      if (e.key === "ArrowUp" && currentIndex === 0) {
        focusBellButton?.()
        return
      }

      const nextIndex = e.key === "ArrowDown"
        ? Math.min(currentIndex + 1, allItems.length - 1)
        : Math.max(currentIndex - 1, 0)

      allItems[nextIndex]?.focus()
      return
    }

    if (!isNavigational) return
    if (e.key !== "Enter" && e.key !== " ") return
    e.stopPropagation()
    handleNavigation(e)
  }

  const handleDeleteKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault()
      e.stopPropagation()

      const allItems = Array.from(document.querySelectorAll(".notifications-list-item:not(.empty)"))
      const currentIndex = allItems.indexOf(itemRef.current)
      if (currentIndex === 0) {
        focusBellButton?.()
      } else {
        allItems[Math.max(currentIndex - 1, 0)]?.focus()
      }
      return
    }

    if (e.key !== "ArrowLeft") return
    e.preventDefault()
    e.stopPropagation()
    itemRef.current?.focus()
  }

  const getBackgroundStyle = () => {
    if ((isHovered || isFocused) && isNavigational) {
      return { background: "#F9F9F9", cursor: "pointer" }
    } else {
      return { background: "white" }
    }
  }

  return (
    <>
      <div
        className={`notifications-list-item ${notification.type}`}
        key={index}
        ref={itemRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleItemFocus}
        onBlur={handleItemBlur}
        style={getBackgroundStyle()}
        role={isNavigational ? "link" : undefined}
        tabIndex={0}
        onClick={handleNavigation}
        onKeyDown={handleItemKeyDown}
      >
        {(isHovered || isFocused) && (
          <div className="notifications-list-item-menu-section">
            <button
              ref={deleteButtonRef}
              className="notifications-list-item-menu-section-button"
              onClick={handleDelete}
              onKeyDown={handleDeleteKeyDown}
              aria-label="Delete notification"
            >
              <FontAwesomeIcon icon={faTimes} color="#40777c" size="xs" />
            </button>
          </div>
        )}

        <div className="notifications-list-item-header">
          <WingsHeading level={6} as="span" className={`notifications-list-item-title ${type}`} underlineColor="var(--w-color-pink-500)">
            {locale === "es-ES" && headingEs ? headingEs : heading}
          </WingsHeading>
          <WingsDetailText as="span" className="notifications-list-item-time">{displayTime}</WingsDetailText>
        </div>
        <WingsDetailText as="div" className="notifications-list-item-body">
          <CleanHTML dirtyHTML={locale === "es-ES" && messageEs ? messageEs : message} />
        </WingsDetailText>
      </div>
    </>
  )
}

export default Notifications
